// View: /calibration — учиться на отклонениях план/факт.
//
// 4 секции:
//   1. Карточки-сводка (sample_size, avg_ratio_pct, stddev_pct, мин/макс).
//   2. Распределение по зонам (cool/good/warn/bad из calibration-zones.js).
//   3. Тренд по неделям (Chart.js bar + line на 100% ориентир).
//   4. Таблица завершённых задач с план/факт/отклонением и rebaseline_count.
//
// Поведение при малых N:
//   - N < 5  : карточки и тренд скрыты, плашка «Накопи ещё».
//   - 5..19  : карточки + тренд + предупреждение «выборка маленькая».
//   - N >= 20: всё полноценно.

import Chart from 'chart.js/auto';
import { api } from '../api.js';
import { ZONE_LABELS, ZONE_ORDER, getZone } from '../utils/calibration-zones.js';
import { formatDateDisplay } from '../utils/date.js';

let _trendChart = null;
let _currentDays = 90;

export async function initCalibration(container) {
  if (_trendChart) { try { _trendChart.destroy(); } catch { /* ignore */ } _trendChart = null; }

  container.innerHTML = `
    <div class="calibration-root">
      <div class="cal-header">
        <h2>🎯 Калибрация — точность оценок</h2>
        <label class="cal-period">
          Период:
          <select id="cal-period-select">
            <option value="30">30 дней</option>
            <option value="90" selected>90 дней</option>
            <option value="180">6 месяцев</option>
            <option value="365">Год</option>
          </select>
        </label>
      </div>
      <div id="cal-content"><div class="loading-spinner">Загрузка калибрации…</div></div>
    </div>
  `;

  const sel = container.querySelector('#cal-period-select');
  sel.addEventListener('change', async (e) => {
    _currentDays = Number(e.target.value);
    await renderContent(container);
  });

  await renderContent(container);
}

async function renderContent(container) {
  const slot = container.querySelector('#cal-content');
  slot.innerHTML = '<div class="loading-spinner">Загрузка калибрации…</div>';

  const settle = (p) => p.then(v => ({ ok: true, v })).catch(e => ({ ok: false, e }));
  const days = _currentDays;
  const [summary, tasks, trend] = await Promise.all([
    settle(api.stats.calibration(days)),
    settle(api.calibration.tasks({ days, limit: 200 })),
    settle(api.calibration.trend({ bucket: 'week', weeks: 12 })),
  ]);

  if (!summary.ok) {
    slot.innerHTML = `<div class="dash-error">Ошибка загрузки сводки: ${esc(summary.e.message)}</div>`;
    return;
  }

  const N = Number(summary.v.sample_size ?? 0);

  if (N < 5) {
    const left = Math.max(5 - N, 0);
    slot.innerHTML = `
      <div class="cal-empty">
        <div class="cal-empty-icon">📊</div>
        <div class="cal-empty-title">Калибрация пока пустая</div>
        <div class="cal-empty-text">
          Завершено задач с факт-временем: <strong>${N}</strong>.
          Минимум для статистики — 5. Осталось накопить: <strong>${left}</strong>.
        </div>
        <div class="cal-empty-hint">
          Калибрация наполняется автоматически: каждая задача со статусом <em>done</em>
          и заполненным <em>actual_minutes_spent</em> попадает в выборку.
          Закрывай задачи через Mentor-бот или таблицу Gantt.
        </div>
      </div>
      ${tasks.ok && tasks.v.length ? `
        <div class="cal-section">
          <h3>Завершённые задачи (${tasks.v.length})</h3>
          ${renderTable(tasks.v)}
        </div>
      ` : ''}
    `;
    return;
  }

  slot.innerHTML = `
    ${renderSummaryCards(summary.v, N)}
    ${renderTrendSection()}
    <div class="cal-section">
      <h3>Завершённые задачи (${tasks.ok ? tasks.v.length : '?'})</h3>
      ${tasks.ok ? renderTable(tasks.v) : `<div class="dash-error">Ошибка: ${esc(tasks.e.message)}</div>`}
    </div>
  `;

  if (trend.ok) renderTrendChart(trend.v);
}

function renderSummaryCards(d, N) {
  const ratio = Number(d.avg_ratio_pct ?? 0);
  const sd    = d.stddev_pct != null ? Number(d.stddev_pct) : null;
  const lowSample = N < 20;
  // Распределение по зонам — считаем на клиенте через единый модуль порогов.
  // Карточки используют только агрегаты (avg/stddev/min/max), для зон нужны
  // данные таблицы — но отрисуем заглушку и обновим в renderTable если оно
  // загрузится позже. Пока сводка по min/max + sample.
  return `
    ${lowSample ? `<div class="cal-warning">⚠ Выборка маленькая (N=${N}). Метрики ориентировочные. Полную статистику дадим при N≥20.</div>` : ''}
    <div class="cal-cards">
      <div class="cal-card">
        <div class="cal-card-key">Завершено задач</div>
        <div class="cal-card-value">${N}</div>
      </div>
      <div class="cal-card">
        <div class="cal-card-key">Средний коэффициент</div>
        <div class="cal-card-value">${ratio.toFixed(0)}%</div>
        <div class="cal-card-hint">${ratio < 100 ? 'обычно успеваем быстрее' : ratio > 100 ? 'обычно перерасход' : 'попадаем в план'}</div>
      </div>
      <div class="cal-card">
        <div class="cal-card-key">Разброс (stddev)</div>
        <div class="cal-card-value">${sd != null ? `±${sd.toFixed(0)}%` : '—'}</div>
      </div>
      <div class="cal-card">
        <div class="cal-card-key">Min / Max</div>
        <div class="cal-card-value">${d.min_ratio_pct ?? '—'}% / ${d.max_ratio_pct ?? '—'}%</div>
      </div>
    </div>
  `;
}

function renderTrendSection() {
  return `
    <div class="cal-section">
      <h3>Тренд точности по неделям</h3>
      <div class="cal-chart-wrap"><canvas id="cal-trend-canvas" height="180"></canvas></div>
    </div>
  `;
}

function renderTrendChart(rows) {
  const canvas = document.getElementById('cal-trend-canvas');
  if (!canvas || !rows?.length) return;
  const labels = rows.map(r => {
    const d = new Date(r.bucket_start);
    return `${d.getDate()}.${(d.getMonth() + 1).toString().padStart(2, '0')}`;
  });
  const values = rows.map(r => Number(r.avg_ratio_pct ?? 0));
  const sizes  = rows.map(r => Number(r.sample_size ?? 0));

  if (_trendChart) { _trendChart.destroy(); _trendChart = null; }
  _trendChart = new Chart(canvas.getContext('2d'), {
    type: 'bar',
    data: {
      labels,
      datasets: [
        {
          label: 'avg_ratio_pct',
          data: values,
          backgroundColor: values.map(v => barColor(v)),
        },
        {
          type: 'line',
          label: '100% ориентир',
          data: values.map(() => 100),
          borderColor: 'rgba(20, 184, 166, 0.7)',  // teal
          borderDash: [4, 4],
          backgroundColor: 'transparent',
          pointRadius: 0,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            afterLabel: (ctx) => `выборка: ${sizes[ctx.dataIndex] ?? 0}`,
          },
        },
      },
      scales: {
        x: { ticks: { color: '#8b949e', font: { size: 10 } } },
        y: {
          ticks: { color: '#8b949e', font: { size: 10 }, callback: (v) => `${v}%` },
          beginAtZero: true,
        },
      },
    },
  });
}

function barColor(ratio) {
  // Roxber Teal palette — синхронно с CSS-токенами zone-cool/good/warn/bad.
  const z = getZone(ratio);
  return ({
    cool: '#4FD1C5',  // мягкий teal — переоценили
    good: '#2DD4BF',  // teal-bright — попали в план
    warn: '#d29922',  // yellow — умеренно
    bad:  '#B71C1C',  // red — большая недооценка
  })[z] ?? '#607D8B';
}

function renderTable(rows) {
  if (!rows.length) return '<div class="dash-error">Нет завершённых задач за период.</div>';
  const zoneCounts = { cool: 0, good: 0, warn: 0, bad: 0 };
  for (const r of rows) {
    const z = getZone(r.ratio_pct);
    if (z) zoneCounts[z]++;
  }
  const dist = ZONE_ORDER.map(z =>
    `<span class="cal-zone-pill cal-zone-${z}" title="${ZONE_LABELS[z]}">${z}: ${zoneCounts[z]}</span>`
  ).join(' ');

  const rowsHtml = rows.map(r => {
    const z = getZone(r.ratio_pct);
    const ratioStr = r.ratio_pct != null ? Number(r.ratio_pct).toFixed(0) + '%' : '—';
    const variance = r.variance_min != null
      ? (Number(r.variance_min) > 0 ? `+${r.variance_min}` : String(r.variance_min))
      : '—';
    const completedStr = r.completed_at ? formatDateDisplay(r.completed_at) : '—';
    const rebaseTag = r.rebaseline_count > 0
      ? `<span class="cal-rebase" title="rebaseline ${r.rebaseline_count}× — текущая оценка ${r.current_baseline_min} мин">↻${r.rebaseline_count}</span>`
      : '';
    return `
      <tr class="cal-row cal-zone-${z ?? 'na'}-row">
        <td>${esc(r.completed_at ? completedStr : '—')}</td>
        <td><a data-route="/gantt/${r.project_id}">${esc(r.name)}</a></td>
        <td>${esc(r.project_name ?? '')}</td>
        <td>${esc(r.resource_name ?? '—')}</td>
        <td class="cal-num">${r.estimate_min ?? '—'}</td>
        <td class="cal-num">${r.actual_min ?? '—'}</td>
        <td class="cal-num">${variance}</td>
        <td class="cal-num"><span class="cal-zone-pill cal-zone-${z ?? 'na'}">${ratioStr}</span></td>
        <td>${rebaseTag}</td>
      </tr>
    `;
  }).join('');

  return `
    <div class="cal-distribution">${dist}</div>
    <div class="cal-table-wrap">
      <table class="cal-table">
        <thead>
          <tr>
            <th>Дата</th>
            <th>Задача</th>
            <th>Проект</th>
            <th>Ресурс</th>
            <th class="cal-num">План</th>
            <th class="cal-num">Факт</th>
            <th class="cal-num">Δ</th>
            <th class="cal-num">%</th>
            <th>Rebase</th>
          </tr>
        </thead>
        <tbody>${rowsHtml}</tbody>
      </table>
    </div>
  `;
}

function esc(s) {
  return String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
